import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { SORT } from '@/components/list-bar/ListBarProfile';
import useCreateQueryString from '@/hooks/useCreateQueryString';
import { useSocket } from '@/provider/SocketProvider';
import type { IGetProcessingAsset, IUpdateMultiTags } from '@/services';
import { getAssetWithId, getProcessingAsset, updateMultiTags } from '@/services';
import type { IAssetDetail, IAssetProcessing } from '@/types/asset';
import { STATUS_PROCESSING } from '@/types/asset';

import { toDataUpdateMultiAssetTags } from './MintAsset/helpers';
import type { DataInputMultiMint } from './MintAsset/useMintAsset';

export const PAGE_SIZE = 20;

const useProcessing = (
  pageSize = 20,
  isForMint = false,
  isHide?: boolean,
  selectedMinted?: number[] | null,
  isInfinityScroll?: boolean,
) => {
  const processRef = useRef<IAssetProcessing[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [processData, setProcessData] = useState<IAssetProcessing[]>([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState(SORT.desc);
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const { socket } = useSocket();
  const [assetId, setAssetId] = useState<number>();
  const [processId, setProcessId] = useState<number | string>();
  const [multiProcessIds, setMultiProcessIds] = useState<Array<number>>([]);
  const searchParam = useSearchParams();
  const pageParam = searchParam.get('page');
  const pageWatch = pageParam ? parseInt(pageParam) : 1;
  const createQueryString = useCreateQueryString();

  useEffect(() => {
    if (assetId && socket && processId) {
      socket.on(`mint-${assetId}`, (message: any) => {
        const result = JSON.parse(message);
        if (result.txHash) {
          multiProcessIds.length > 0
            ? updateMultiStatusProcessing(multiProcessIds, STATUS_PROCESSING.Minted)
            : updateAssetForProcessing(processId, assetId);
        }
      });
    }

    return () => {
      socket && assetId && socket.off(`mint-${assetId}`);
    };
  }, [assetId, processId, multiProcessIds]);

  const handleGetProcessing = async (page: number) => {
    setLoading(true);
    const queryParams: IGetProcessingAsset = {
      size: pageSize,
      offset: page ? (Number(page) - 1) * pageSize : 0,
    };
    queryParams.filter = `${encodeURIComponent(`{"hide": ${isHide}}`)}`;
    if (sort) {
      queryParams.sort = `${encodeURIComponent(`{"createdAt": "${sort}"}`)}`;
    }
    if (isForMint) {
      queryParams.filter = `${encodeURIComponent(`{"status":[1]}`)}`;
    }
    if (selectedMinted) {
      queryParams.filter = `${encodeURIComponent(
        `{"status":[${selectedMinted}], "hide": ${isHide}}`,
      )}`;
    }
    if (search) {
      queryParams.filter = `${encodeURIComponent(`{"name": "${search}"}`)}`;
    }
    const res = await getProcessingAsset(queryParams);

    if (page === 1) {
      processRef.current = [];
      setProcessData([]);
    }
    if (res?.data?.items?.length === 0) {
      setHasMore(false);
    }
    if (isInfinityScroll) {
      processRef.current = processRef.current.concat(res?.data?.items || []);
      setProcessData(processRef.current);
    } else {
      setTotalItems(res?.data?.total);
      setProcessData(res?.data?.items || []);
    }
    setLoading(false);
  };

  const loadMore = (page: number) => {
    setCurrentPage(page);
    handleGetProcessing(page);
  };

  useEffect(() => {
    if (isInfinityScroll) {
      setHasMore(true);
      setCurrentPage(1);
      handleGetProcessing(1);
    } else {
      createQueryString(['tab', 'page'], ['processing', 1], { scroll: false });
      handleGetProcessing(1);
    }
  }, [search, sort, isHide]);

  useEffect(() => {
    if (!isInfinityScroll && pageWatch > 1) handleGetProcessing(pageWatch);
  }, [pageWatch]);

  const updateStatusProcessing = (processId: number | string, status: STATUS_PROCESSING) => {
    const newData: IAssetProcessing[] = processData.map((item) => {
      if (item.id === processId) return { ...item, status };
      return item;
    });
    processRef.current = newData;
    setProcessData(newData);
  };

  const updateAssetForProcessing = async (processId: number | string, assetId: number) => {
    const res = await getAssetWithId(assetId);
    const asset: IAssetDetail = res.data;
    if (!asset) return;
    const newData = processData.map((item) => {
      if (item.id === processId)
        return {
          ...item,
          status: STATUS_PROCESSING.Minted,
          name: asset.name,
          description: asset.description,
          tokenId: asset.id,
          asset,
        };
      return item;
    });
    toast.success('Minted artwork successfully!');
    processRef.current = newData as IAssetProcessing[];
    setProcessData(newData as IAssetProcessing[]);
  };

  const handleAfterUpdated = (processId: number | string, assetId: number) => {
    updateStatusProcessing(processId, STATUS_PROCESSING.Minting);
    setAssetId(assetId);
    setProcessId(processId);
  };

  const updateMultiStatusProcessing = (processIds: Array<number>, status: STATUS_PROCESSING) => {
    const newData: IAssetProcessing[] = processData.map((item) => {
      if (processIds.includes(item.id)) return { ...item, status };
      return item;
    });
    processRef.current = newData;
    setProcessData(newData);
  };

  const updateMultiInfoProcessing = (data: DataInputMultiMint[], status: STATUS_PROCESSING) => {
    const newData: IAssetProcessing[] = processData.map((item) => {
      const process = data.find((dt) => dt.processId === item.id);
      if (process)
        return {
          ...item,
          status,
          name: process.asset.name,
          description: process.asset.description,
          tokenId: process.asset.tokenId,
        };
      return item;
    });
    processRef.current = newData;
    setProcessData(newData);
  };

  const updateMultiAssetTags = async (data: IUpdateMultiTags[]) => {
    await updateMultiTags(data);
  };

  const handleAfterMultiMint = (data: DataInputMultiMint[]) => {
    if (data.length === 0) return;
    setMultiProcessIds(data.map((dt) => dt.processId));
    updateMultiInfoProcessing(data, STATUS_PROCESSING.Minting);
    const firstAssetId = data[0]?.asset?.tokenId || 0;
    const firstProcessId = data[0]?.processId || 0;
    setAssetId(Number(firstAssetId));
    setProcessId(firstProcessId);
    updateMultiAssetTags(toDataUpdateMultiAssetTags(data));
  };

  return {
    currentPage,
    setCurrentPage,
    processList: processData,
    setProcessData,
    loading,
    search,
    setSearch,
    sort,
    setSort,
    hasMore,
    loadMore,
    handleAfterUpdated,
    handleAfterMultiMint,
    totalItems,
  };
};

export default useProcessing;
